import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Product, Order, Category, Table, Customer } from '../types';
import { mockOrders, mockTables, mockCustomers } from '../data/mockData';
import { FirestoreService } from '../firebase/firestore';

interface DataContextType {
  products: Product[];
  orders: Order[];
  categories: Category[];
  tables: Table[];
  customers: Customer[];
  updateProducts: (products: Product[]) => void;
  updateOrders: (orders: Order[]) => void;
  updateCategories: (categories: Category[]) => void;
  updateTables: (tables: Table[]) => Promise<void>;
  updateCustomers: (customers: Customer[]) => void;
  addCustomer: (customer: Customer) => void;
  deleteCustomer: (customerId: string) => Promise<void>;
  refreshCustomers: () => Promise<void>;
  refreshProducts: () => Promise<void>;
  refreshOrders: () => Promise<void>;
  refreshCategories: () => Promise<void>;
  refreshTables: () => Promise<void>;
  refreshAllData: () => Promise<void>;
  reduceProductStock: (orderItems: { id: string; quantity: number }[]) => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Load data from Firestore and localStorage on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('🔄 DataContext: Loading data from Firestore...');
        
        // Load products from Firestore
        const firestoreProducts = await FirestoreService.getProducts();
        console.log('📦 DataContext: Products from Firestore:', firestoreProducts);
        
        // Only use Firebase data, no fallback to mock data
        setProducts(firestoreProducts);
        console.log('✅ DataContext: Products loaded from Firestore');
        
        if (firestoreProducts.length === 0) {
          console.log('⚠️ DataContext: No products in Firestore');
        }
        
        // Load categories from Firestore
        const firestoreCategories = await FirestoreService.getCategories();
        console.log('📂 DataContext: Categories from Firestore:', firestoreCategories);
        
        // Only use Firebase data, no fallback to mock data
        setCategories(firestoreCategories);
        console.log('✅ DataContext: Categories loaded from Firestore');
        
        if (firestoreCategories.length === 0) {
          console.log('⚠️ DataContext: No categories in Firestore');
        }
        
        // Load other data from localStorage (for now)
        const savedOrders = localStorage.getItem('bookfood_orders');
        const savedTables = localStorage.getItem('bookfood_tables');
        
        console.log('📦 DataContext: Saved orders from localStorage:', savedOrders);
        console.log('📦 DataContext: Mock orders:', mockOrders);
        console.log('🪑 DataContext: Saved tables from localStorage:', savedTables);
        console.log('🪑 DataContext: Mock tables:', mockTables);

        // Load tables from Firestore
        const firestoreTables = await FirestoreService.getTables();
        console.log('🪑 DataContext: Tables from Firestore:', firestoreTables);
        console.log('🪑 DataContext: Number of tables from Firestore:', firestoreTables.length);

        // Load orders from Firestore
        const firestoreOrders = await FirestoreService.getOrders();
        console.log('📦 DataContext: Orders from Firestore:', firestoreOrders);
        console.log('📦 DataContext: Number of orders from Firestore:', firestoreOrders.length);
        
        if (firestoreOrders.length > 0) {
          setOrders(firestoreOrders);
          console.log('✅ DataContext: Orders loaded from Firestore');
        } else {
          // Fallback to localStorage if no orders in Firestore
          const ordersData = savedOrders ? JSON.parse(savedOrders) : mockOrders;
          console.log('📦 DataContext: Orders from localStorage/mock:', ordersData);
          console.log('📦 DataContext: Number of orders from localStorage/mock:', ordersData.length);
          setOrders(ordersData);
          console.log('⚠️ DataContext: No orders in Firestore, using localStorage/mock data');
        }
        
        try {
          if (firestoreTables.length > 0) {
            setTables(firestoreTables);
            console.log('✅ DataContext: Tables loaded from Firestore');
          } else {
            // Fallback to localStorage if no tables in Firestore
            const tablesData = savedTables ? JSON.parse(savedTables) : mockTables;
            console.log('🪑 DataContext: Loading tables from localStorage/mock:', tablesData);
            console.log('🪑 DataContext: Number of tables loaded:', tablesData.length);
            setTables(tablesData);
            console.log('⚠️ DataContext: No tables in Firestore, using localStorage/mock data');
          }
        } catch (error) {
          console.error('❌ DataContext: Error loading tables, using mock data:', error);
          setTables(mockTables);
        }
        
        // Mark initial load as complete
        setIsInitialLoad(false);
        
        // Load customers from Firestore
        const firestoreCustomers = await FirestoreService.getCustomers();
        console.log('👥 DataContext: Customers from Firestore:', firestoreCustomers);
        console.log('👥 DataContext: Number of customers from Firestore:', firestoreCustomers.length);
        
        if (firestoreCustomers.length > 0) {
          setCustomers(firestoreCustomers);
          console.log('✅ DataContext: Customers loaded from Firestore');
        } else {
          // Fallback to localStorage if no customers in Firestore
          const savedCustomers = localStorage.getItem('bookfood_customers');
          const customersData = savedCustomers ? JSON.parse(savedCustomers) : mockCustomers;
          console.log('👥 DataContext: Customers from localStorage:', customersData);
          console.log('👥 DataContext: Number of customers from localStorage:', customersData.length);
          setCustomers(customersData);
          console.log('⚠️ DataContext: No customers in Firestore, using localStorage/mock data');
        }
      } catch (error) {
        console.error('❌ DataContext: Error loading data from Firestore:', error);
        // Set empty arrays on error instead of using mock data
        setProducts([]);
        setCategories([]);
        console.log('⚠️ DataContext: Error loading from Firestore, using empty data');
      }
    };

    loadData();

    // Listen for storage changes to update customers when new ones are added
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'bookfood_customers') {
        const savedCustomers = localStorage.getItem('bookfood_customers');
        setCustomers(savedCustomers ? JSON.parse(savedCustomers) : []);
      }
    };

    // Also listen for custom events for same-tab updates
    const handleCustomStorageChange = () => {
      const savedCustomers = localStorage.getItem('bookfood_customers');
      const customersData = savedCustomers ? JSON.parse(savedCustomers) : [];
      console.log('DataContext: Received custom storage change, customers:', customersData);
      setCustomers(customersData);
    };

    // Listen for customer registration events to refresh from Firestore
    const handleCustomerRegistered = async () => {
      console.log('🔄 DataContext: Customer registered, refreshing from Firestore...');
      await refreshCustomers();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('customStorageChange', handleCustomStorageChange);
    window.addEventListener('customerRegistered', handleCustomerRegistered);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('customStorageChange', handleCustomStorageChange);
      window.removeEventListener('customerRegistered', handleCustomerRegistered);
    };
  }, []);

  // Save to localStorage whenever data changes (but not during initial load)
  useEffect(() => {
    if (!isInitialLoad) {
      localStorage.setItem('bookfood_products', JSON.stringify(products));
    }
  }, [products, isInitialLoad]);

  useEffect(() => {
    if (!isInitialLoad) {
      localStorage.setItem('bookfood_orders', JSON.stringify(orders));
    }
  }, [orders, isInitialLoad]);

  useEffect(() => {
    if (!isInitialLoad) {
      localStorage.setItem('bookfood_categories', JSON.stringify(categories));
    }
  }, [categories, isInitialLoad]);

  useEffect(() => {
    // Don't save to localStorage during initial load to prevent overwriting existing data
    if (isInitialLoad) {
      console.log('🪑 DataContext: Skipping tables save during initial load');
      return;
    }
    
    try {
      console.log('🪑 DataContext: Saving tables to localStorage:', tables);
      localStorage.setItem('bookfood_tables', JSON.stringify(tables));
      console.log('✅ DataContext: Tables saved to localStorage successfully');
    } catch (error) {
      console.error('❌ DataContext: Error saving tables to localStorage:', error);
    }
  }, [tables, isInitialLoad]);

  useEffect(() => {
    if (!isInitialLoad) {
      localStorage.setItem('bookfood_customers', JSON.stringify(customers));
    }
  }, [customers, isInitialLoad]);

  const updateProducts = (newProducts: Product[]) => {
    setProducts(newProducts);
  };

  const updateOrders = (newOrders: Order[]) => {
    setOrders(newOrders);
  };

  const updateCategories = (newCategories: Category[]) => {
    setCategories(newCategories);
  };

  const updateTables = async (newTables: Table[]) => {
    console.log('🪑 DataContext: Updating tables - Old count:', tables.length, 'New count:', newTables.length);
    
    // Save to Firebase first, then update local state with Firebase IDs
    try {
      console.log('🪑 DataContext: Syncing with Firebase...');
      
      // Get current tables from Firebase
      const existingTables = await FirestoreService.getTables();
      console.log('🪑 DataContext: Firebase has', existingTables.length, 'tables');
      
      // Find tables to delete (exist in Firebase but not in new list)
      const tablesToDelete = existingTables.filter(existing => 
        !newTables.some(newT => newT.id === existing.id)
      );
      
      // Find tables to add (exist in new list but not in Firebase)
      const tablesToAdd = newTables.filter(newT => 
        !existingTables.some(existing => existing.id === newT.id)
      );
      
      // Find tables to update (exist in both but might have changes)
      const tablesToUpdate = newTables.filter(newT => {
        const existing = existingTables.find(existing => existing.id === newT.id);
        return existing && (
          existing.tableNumber !== newT.tableNumber || 
          existing.location !== newT.location || 
          existing.capacity !== newT.capacity ||
          existing.status !== newT.status ||
          existing.currentCustomerName !== newT.currentCustomerName ||
          existing.currentCustomerId !== newT.currentCustomerId ||
          existing.sessionStartTime !== newT.sessionStartTime
        );
      });
      
      console.log('🪑 DataContext: To delete:', tablesToDelete.length, 'To add:', tablesToAdd.length, 'To update:', tablesToUpdate.length);
      
      // Delete tables from Firebase
      for (const table of tablesToDelete) {
        if (table.id) {
          console.log('🗑️ DataContext: Deleting from Firebase:', table.id);
          await FirestoreService.deleteTable(table.id);
        }
      }
      
      // Track ID mappings for newly added tables
      const idMappings: { [oldId: string]: string } = {};
      
      // Add new tables to Firebase
      for (const table of tablesToAdd) {
        console.log('➕ DataContext: Adding to Firebase:', table);
        const firebaseId = await FirestoreService.addTable({
          tableNumber: table.tableNumber,
          location: table.location,
          capacity: table.capacity,
          status: table.status
        });
        
        if (!firebaseId) {
          console.error('❌ DataContext: Failed to add table to Firebase, no ID returned');
          throw new Error('Failed to add table to Firebase');
        }
        
        // Track the ID mapping
        if (firebaseId !== table.id) {
          console.log('🔄 DataContext: Mapping local ID', table.id, 'to Firebase ID', firebaseId);
          idMappings[table.id] = firebaseId;
        }
      }
      
      // Update existing tables in Firebase
      for (const table of tablesToUpdate) {
        if (table.id) {
          console.log('✏️ DataContext: Updating in Firebase:', table.id);
          await FirestoreService.updateTable(table.id, {
            tableNumber: table.tableNumber,
            location: table.location,
            capacity: table.capacity,
            status: table.status,
            currentCustomerName: table.currentCustomerName,
            currentCustomerId: table.currentCustomerId,
            sessionStartTime: table.sessionStartTime
          });
        }
      }
      
      // Update local state with Firebase IDs
      const updatedTables = newTables.map(t => {
        if (idMappings[t.id]) {
          return { ...t, id: idMappings[t.id] };
        }
        return t;
      });
      
      console.log('✅ DataContext: Firebase sync completed');
      console.log('🪑 DataContext: Setting tables with Firebase IDs:', updatedTables);
      setTables(updatedTables);
      
    } catch (error) {
      console.error('❌ DataContext: Error syncing with Firebase:', error);
      // On error, still update local state
      setTables(newTables);
    }
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('customStorageChange'));
  };

  const updateCustomers = (newCustomers: Customer[]) => {
    setCustomers(newCustomers);
  };

  const addCustomer = (customer: Customer) => {
    setCustomers(prev => [...prev, customer]);
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('customStorageChange'));
  };

  const deleteCustomer = async (customerId: string) => {
    try {
      console.log('🗑️ DataContext: Deleting customer from Firestore...', customerId);
      const success = await FirestoreService.deleteCustomer(customerId);
      
      if (success) {
        console.log('✅ DataContext: Customer deleted from Firestore');
        setCustomers(prev => prev.filter(customer => customer.id !== customerId));
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('customStorageChange'));
      } else {
        console.error('❌ DataContext: Failed to delete customer from Firestore');
        // Fallback to local only
        setCustomers(prev => prev.filter(customer => customer.id !== customerId));
        window.dispatchEvent(new CustomEvent('customStorageChange'));
      }
    } catch (error) {
      console.error('❌ DataContext: Error deleting customer:', error);
      // Fallback to local only
      setCustomers(prev => prev.filter(customer => customer.id !== customerId));
      window.dispatchEvent(new CustomEvent('customStorageChange'));
    }
  };

  const refreshCustomers = async () => {
    try {
      console.log('🔄 DataContext: Refreshing customers from Firestore...');
      const firestoreCustomers = await FirestoreService.getCustomers();
      console.log('👥 DataContext: Refreshed customers from Firestore:', firestoreCustomers);
      console.log('👥 DataContext: Number of refreshed customers:', firestoreCustomers.length);
      
      if (firestoreCustomers.length > 0) {
        setCustomers(firestoreCustomers);
        console.log('✅ DataContext: Customers refreshed from Firestore');
      } else {
        console.log('⚠️ DataContext: No customers found in Firestore during refresh');
        // Also check localStorage as fallback
        const savedCustomers = localStorage.getItem('bookfood_customers');
        const customersData = savedCustomers ? JSON.parse(savedCustomers) : mockCustomers;
        console.log('👥 DataContext: Fallback to localStorage customers:', customersData);
        setCustomers(customersData);
      }
    } catch (error) {
      console.error('❌ DataContext: Error refreshing customers:', error);
    }
  };

  const refreshProducts = async () => {
    try {
      console.log('🔄 DataContext: Refreshing products from Firestore...');
      const firestoreProducts = await FirestoreService.getProducts();
      console.log('📦 DataContext: Refreshed products from Firestore:', firestoreProducts);
      
      // Always use Firebase data, even if empty
      setProducts(firestoreProducts);
      console.log('✅ DataContext: Products refreshed from Firestore');
      
      if (firestoreProducts.length === 0) {
        console.log('⚠️ DataContext: No products found in Firestore during refresh');
      }
    } catch (error) {
      console.error('❌ DataContext: Error refreshing products:', error);
      setProducts([]);
    }
  };

  const refreshOrders = async () => {
    try {
      console.log('🔄 DataContext: Refreshing orders from Firestore...');
      const firestoreOrders = await FirestoreService.getOrders();
      console.log('📦 DataContext: Refreshed orders from Firestore:', firestoreOrders);
      
      if (firestoreOrders.length > 0) {
        setOrders(firestoreOrders);
        console.log('✅ DataContext: Orders refreshed from Firestore');
      } else {
        console.log('⚠️ DataContext: No orders found in Firestore during refresh');
      }
    } catch (error) {
      console.error('❌ DataContext: Error refreshing orders:', error);
    }
  };

  const refreshCategories = async () => {
    try {
      console.log('🔄 DataContext: Refreshing categories from Firestore...');
      const firestoreCategories = await FirestoreService.getCategories();
      console.log('📂 DataContext: Refreshed categories from Firestore:', firestoreCategories);
      
      // Always use Firebase data, even if empty
      setCategories(firestoreCategories);
      console.log('✅ DataContext: Categories refreshed from Firestore');
      
      if (firestoreCategories.length === 0) {
        console.log('⚠️ DataContext: No categories found in Firestore during refresh');
      }
    } catch (error) {
      console.error('❌ DataContext: Error refreshing categories:', error);
      setCategories([]);
    }
  };

  const refreshTables = async () => {
    try {
      console.log('🔄 DataContext: Refreshing tables from Firebase...');
      const firestoreTables = await FirestoreService.getTables();
      console.log('🪑 DataContext: Refreshed tables from Firebase:', firestoreTables);
      console.log('🪑 DataContext: Number of refreshed tables:', firestoreTables.length);
      
      // Always use Firebase data, even if empty
      setTables(firestoreTables);
      console.log('✅ DataContext: Tables refreshed from Firebase');
      
      if (firestoreTables.length === 0) {
        console.log('⚠️ DataContext: No tables found in Firebase during refresh');
      }
    } catch (error) {
      console.error('❌ DataContext: Error refreshing tables:', error);
      // On error, try localStorage as fallback
      const savedTables = localStorage.getItem('bookfood_tables');
      const tablesData = savedTables ? JSON.parse(savedTables) : mockTables;
      setTables(tablesData);
    }
  };

  const refreshAllData = async () => {
    try {
      console.log('🔄 DataContext: Refreshing all data from Firestore...');
      await Promise.all([
        refreshCustomers(),
        refreshProducts(),
        refreshOrders(),
        refreshCategories()
      ]);
      console.log('✅ DataContext: All data refreshed from Firestore');
    } catch (error) {
      console.error('❌ DataContext: Error refreshing all data:', error);
    }
  };

  const reduceProductStock = async (orderItems: { id: string; quantity: number }[]) => {
    console.log('📦 DataContext: Reducing product stock for order items:', orderItems);
    
    // Update local state first
    setProducts(prev => {
      const updatedProducts = prev.map(product => {
        const orderItem = orderItems.find(item => item.id === product.id);
        if (orderItem) {
          const newStock = Math.max(0, product.stock - orderItem.quantity);
          console.log(`📦 DataContext: Reducing stock for ${product.name} from ${product.stock} to ${newStock}`);
          return { ...product, stock: newStock };
        }
        return product;
      });
      return updatedProducts;
    });

    // Update stock in Firestore
    try {
      for (const orderItem of orderItems) {
        const product = products.find(p => p.id === orderItem.id);
        if (product) {
          const newStock = Math.max(0, product.stock - orderItem.quantity);
          console.log(`💾 DataContext: Updating stock in Firestore for ${product.name} to ${newStock}`);
          await FirestoreService.updateProduct(orderItem.id, { stock: newStock });
        }
      }
      console.log('✅ DataContext: Stock updated successfully in Firestore');
    } catch (error) {
      console.error('❌ DataContext: Error updating stock in Firestore:', error);
      // Note: Local state is already updated, so the UI will reflect the change
      // even if Firestore update fails
    }
  };

  const value = {
    products,
    orders,
    categories,
    tables,
    customers,
    updateProducts,
    updateOrders,
    updateCategories,
    updateTables,
    updateCustomers,
    addCustomer,
    deleteCustomer,
    refreshCustomers,
    refreshProducts,
    refreshOrders,
    refreshCategories,
    refreshTables,
    refreshAllData,
    reduceProductStock
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
